// firestore.rules → RuleSet. Every match block (at any depth) whose full path matches the
// document is a candidate; any of its `allow` lines for the method that evaluates to true allows.
import { matchSegs, type Method, type QueryRequest, type RuleRequest, type RuleSet, type RuleTrace } from "@estra/server";
import type { FieldFilter, Filter } from "@estra/schema";
import type { Allow, Fn, Match, RulesFile, Seg } from "./ast.ts";
import { ev, resourceValue, type Scope } from "./eval.ts";
import { parseRules } from "./parse.ts";
import { docPath, fromWire, RMap, RPath, RSymMap, RTimestamp, ROOT, RuleError, RUnknown, UNKNOWN, type Constraint, type Value } from "./values.ts";

const GROUP: Record<Method, string[]> = {
  get: ["read", "get"],
  list: ["read", "list"],
  create: ["write", "create"],
  update: ["write", "update"],
  delete: ["write", "delete"],
};

interface Entry {
  segs: Seg[];
  allows: Allow[];
  /** Functions visible here, outermost first (inner definitions win). */
  fns: Fn[][];
  line: number;
}

function flatten(file: RulesFile): Entry[] {
  const out: Entry[] = [];
  const walk = (m: Match, prefix: Seg[], fns: Fn[][]) => {
    const segs = [...prefix, ...m.segs];
    const visible = [...fns, m.fns];
    out.push({ segs, allows: m.allows, fns: visible, line: m.line });
    for (const c of m.children) walk(c, segs, visible);
  };
  for (const m of file.matches) walk(m, [], [file.fns]);
  return out;
}

export interface CompileOptions {
  /** firebase.storage rules: the bucket name bound to /b/{bucket} (default "default"). */
  bucket?: string;
  /**
   * How list queries are checked. "firestore" (default): like Firestore, the query's filters must
   * prove every possible result is allowed; nothing is checked per row. "rows": run the query and
   * check each returned document (accepts queries Firestore would refuse).
   */
  listMode?: "firestore" | "rows";
  /** Called with the reason when a condition errors (debugging; never logs data). */
  onError?: (info: { method: Method; path: string; line: number; message: string }) => void;
}

export function compileRules(source: string | RulesFile, opts: CompileOptions = {}): RuleSet {
  const file = typeof source === "string" ? parseRules(source) : source;
  // firebase.storage: paths live under /b/{bucket}/o and resources are object metadata
  // (name, size, contentType, metadata, timeCreated …) rather than {data, id}.
  const storage = file.service === "firebase.storage";
  if (!storage && file.service !== "cloud.firestore") throw new Error(`service "${file.service}" isn't cloud.firestore or firebase.storage`);
  const root = storage ? ["b", opts.bucket ?? "default", "o"] : ROOT;
  const resVal = (d: RuleRequest["resource"]): Value => (storage ? (d ? new RMap({ ...(fromWire(d.data) as RMap).o, name: d.path }) : null) : resourceValue(d));
  const pathVal = (p: string) => (storage ? new RPath([...root, ...p.split("/")]) : docPath(p));
  const entries = flatten(file);
  const restNames = (segs: Seg[]) => new Set(segs.flatMap((s) => ("name" in s && s.rest ? [s.name] : [])));
  // Source lines, for traces (the simulator shows what each step was).
  const lines = typeof source === "string" ? source.split("\n") : null;
  const textAt = (line: number) => (lines?.[line - 1]?.trim() || `line ${line}`).slice(0, 200);

  /** Evaluates one entry's allows for `method`; true only if a condition is definitely true. */
  const evalEntry = async (en: Entry, method: Method, params: Record<string, Value>, request: Value, resource: Value, lookup: RuleRequest["lookup"], path: string, trace?: RuleTrace) => {
    const allows = en.allows.filter((a) => a.methods.some((m) => GROUP[method].includes(m)));
    if (!allows.length) return false;
    trace?.entries.push({ line: en.line, kind: "match", text: textAt(en.line) });
    let scope: Scope | undefined;
    for (const level of en.fns) {
      const next: Scope = { vars: new Map(), fns: new Map(), ...(scope ? { parent: scope } : {}) };
      for (const f of level) next.fns.set(f.name, { fn: f, scope: next });
      scope = next;
    }
    const vars = new Map<string, Value>([["request", request], ["resource", resource], ...Object.entries(params)]);
    const root: Scope = { vars, fns: new Map(), ...(scope ? { parent: scope } : {}) };
    // Wildcards must be visible inside functions defined in the same blocks.
    for (let x = scope; x; x = x.parent) for (const [k, v] of vars) if (!x.vars.has(k)) x.vars.set(k, v);
    const decided = (a: Allow) => {
      trace?.entries.push({ line: a.line, kind: "allow", text: textAt(a.line), result: true });
      if (trace) trace.decidingLine = a.line;
      return true;
    };
    for (const a of allows) {
      if (!a.cond) return decided(a);
      try {
        if ((await ev(a.cond, root, { lookup, depth: 0, ...(trace ? { trace: { entries: trace.entries, line: a.line } } : {}) })) === true) return decided(a);
        trace?.entries.push({ line: a.line, kind: "allow", text: textAt(a.line), result: false });
      } catch (e) {
        if (!(e instanceof RuleError) && !(e instanceof Error)) throw e;
        opts.onError?.({ method, path, line: a.line, message: (e as Error).message });
        trace?.entries.push({ line: a.line, kind: "error", text: (e as Error).message.slice(0, 300), result: false });
      }
    }
    return false;
  };

  return {
    async allowQuery(q: QueryRequest) {
      if (opts.listMode === "rows" || storage) return undefined;
      const branches = dnf(q.where);
      if (!branches) return undefined; // too many OR branches to check statically: fall back to rows
      const coll = q.collection.split("/");
      for (const branch of branches) {
        const { known, cons, id } = pin(branch);
        const segs: QSeg[] = q.group ? [...ROOT, REST, q.collection, id ?? ID] : [...ROOT, ...coll, id ?? ID];
        const request = new RMap({
          auth: q.auth ? new RMap({ uid: q.auth.uid, token: fromWire(q.auth.token) }) : null,
          resource: null,
          time: new RTimestamp(q.time),
          method: "list",
          path: UNKNOWN,
          query: new RMap({ limit: q.limit ?? null, offset: null, orderBy: null }),
        });
        const resource = new RMap({ data: new RSymMap(known, cons), id: id ?? UNKNOWN, __name__: UNKNOWN });
        let ok = false;
        for (const en of entries) {
          const params = matchQuery(en.segs, segs);
          if (params && (await evalEntry(en, "list", params, request, resource, q.lookup, q.collection, q.trace))) {
            ok = true;
            break;
          }
        }
        if (!ok) return false;
      }
      return true;
    },

    async allow(req: RuleRequest) {
      const full = [...root, ...req.path.split("/")];
      let request: Value | undefined;
      for (const en of entries) {
        if (!en.allows.some((a) => a.methods.some((m) => GROUP[req.method].includes(m)))) continue;
        const raw = matchSegs(en.segs, full);
        if (!raw) continue;
        request ??= requestValue(req, resVal, pathVal);
        const rest = restNames(en.segs);
        const params = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, rest.has(k) ? new RPath(v.split("/").filter(Boolean)) : v] as [string, Value]));
        if (await evalEntry(en, req.method, params, request, resVal(req.resource), req.lookup, req.path, req.trace)) return true;
      }
      return false;
    },
  };
}

function requestValue(req: RuleRequest, resVal: (d: RuleRequest["after"]) => Value, pathVal: (p: string) => Value): Value {
  return new RMap({
    auth: req.auth ? new RMap({ uid: req.auth.uid, token: fromWire(req.auth.token) }) : null,
    resource: resVal(req.after),
    time: new RTimestamp(req.time),
    method: req.method,
    path: pathVal(req.path),
    query: new RMap({ limit: req.query?.limit ?? null, offset: null, orderBy: null }),
  });
}

// ---- query checks -------------------------------------------------------------------------

/** Placeholders in a query's "document path": an unknown id, or unknown parents (groups). */
const ID = Symbol("id");
const REST = Symbol("rest");
type QSeg = string | typeof ID | typeof REST;

function matchQuery(pat: Seg[], segs: QSeg[], i = 0, j = 0, out: Record<string, Value> = {}): Record<string, Value> | null {
  if (i === pat.length) return j === segs.length ? out : null;
  const p = pat[i]!;
  const s = segs[j];
  if ("lit" in p) return s === p.lit ? matchQuery(pat, segs, i + 1, j + 1, out) : null;
  if (!p.rest) {
    if (s === undefined || s === REST) return null; // a group query needs a recursive wildcard
    return matchQuery(pat, segs, i + 1, j + 1, { ...out, [p.name]: s === ID ? UNKNOWN : s });
  }
  for (let k = segs.length; k >= j; k--) {
    const part = segs.slice(j, k);
    const v: Value = part.some((x) => typeof x !== "string") ? UNKNOWN : new RPath(part as string[]);
    const r = matchQuery(pat, segs, i + 1, k, { ...out, [p.name]: v });
    if (r) return r;
  }
  return null;
}

const MAX_BRANCHES = 16;

/** Filter → OR of ANDs of field filters (null when it would exceed MAX_BRANCHES). */
function dnf(f: Filter | undefined): FieldFilter[][] | null {
  if (!f) return [[]];
  if ("and" in f) {
    let acc: FieldFilter[][] | null = [[]];
    for (const x of f.and) {
      const d = dnf(x);
      if (!d || !acc) return null;
      acc = acc.flatMap((a) => d.map((b) => [...a, ...b]));
      if (acc.length > MAX_BRANCHES) return null;
    }
    return acc;
  }
  if ("or" in f) {
    const out: FieldFilter[][] = [];
    for (const x of f.or) {
      const d = dnf(x);
      if (!d) return null;
      out.push(...d);
      if (out.length > MAX_BRANCHES) return null;
    }
    return out;
  }
  return [[f]];
}

/** What one AND-branch pins: exact values, value sets, ranges, array membership, the id. */
function pin(branch: FieldFilter[]) {
  const known: Record<string, Value> = {};
  const cons: Record<string, Constraint> = {};
  let id: string | undefined;
  const con = (k: string) => (cons[k] ??= {});
  for (const { field, op, value } of branch) {
    const v = fromWire(value);
    if (field === "id" || field === "__name__") {
      if (op === "==" && typeof v === "string") id = v;
      continue;
    }
    switch (op) {
      case "==":
        known[field] = v;
        break;
      case "in":
        if (Array.isArray(v)) {
          if (v.length === 1) known[field] = v[0]!;
          else con(field).in = v;
        }
        break;
      case "<":
      case "<=":
        con(field).hi = { v, incl: op === "<=" };
        break;
      case ">":
      case ">=":
        con(field).lo = { v, incl: op === ">=" };
        break;
      case "array-contains":
        con(field).contains = [v];
        break;
      case "array-contains-any":
        if (Array.isArray(v)) con(field).contains = v;
        break;
    }
  }
  return { known, cons, id };
}

export { RUnknown };
